import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Row,
  Card,
  CardBody,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import toast from "react-hot-toast";
import PrintHeader from "../../Components/PrintHeader/PrintHeader";
import NumberCommas from "../../Components/NumberCommas/NumberCommas";
import LayoutWithBreadCrumb from "../../pages/Pages/Starter/LayoutWithBreadCrumb";
import DaybookServices from "../../Services/QRCodeServices/DaybookServices/DaybookServices";
import { formatDate } from "../../Components/FormatDate/FormatDate";

const Daybook = () => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [daybookData, setDaybookData] = useState({
    cashOpeningBalance: 0,
    cashAdded: 0,
    dayBookSales: {
      cashSales: 0,
      cardSales: 0,
      bankSales: [],
      totalBankSales: 0,
      creditSales: 0,
      totalSales: 0,
    },
    bankDeposit: 0,
    debtiorsRecived: 0,
    debtiorsRecivedBank: 0,
    cashClosingBalance: 0,
  });

  // State for the accordion
  const [isSalesOpen, setIsSalesOpen] = useState(false);
  const toggleSales = () => setIsSalesOpen(!isSalesOpen);

  // State for the bank sales modal
  const [isBankSalesModalOpen, setIsBankSalesModalOpen] = useState(false);
  const toggleBankSalesModal = () =>
    setIsBankSalesModalOpen(!isBankSalesModalOpen);

  async function fetchDaybookData() {
    try {
      const response = await DaybookServices.getList(selectedDate);
      setDaybookData(response);
    } catch (error) {
      toast.error(error.response?.data || "Error fetching Daybook details");
    }
  }

  // Handle date change
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  useEffect(() => {
    fetchDaybookData();
  }, [selectedDate]);

  const handlePrint = () => {
    window.print();
  };

  // Determine closing balance color based on comparison with opening balance
  const getClosingBalanceColor = () => {
    return daybookData.cashClosingBalance < daybookData.cashOpeningBalance
      ? "#FF9999" // Red if closing is less than opening
      : "#E0DEF6"; // Blue otherwise
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const organizationName = user.organizationInfo.name;

  return (
    <React.Fragment>
      <LayoutWithBreadCrumb title="Daybook" pageTitle="Daily Sales Report">
        <PrintHeader reportTitle="Daily Sales Report" />
        {/* <Row className="mb-2 d-print-none">
          <Col className="d-flex justify-content-end align-items-center">
            <Button
              color="outline-secondary"
              className="py-2 px-3 d-flex align-items-center shadow-sm ms-auto"
              onClick={handlePrint}
            >
              <i className="ri-printer-line fs-16 me-2"></i>
            </Button>
          </Col>
        </Row> */}

        <Row className="mb-2 d-print-none">
          <Col md={4}>
            <FormGroup>
              <Label for="dateFilter">Select Date</Label>
              <Input
                id="dateFilter"
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="form-control"
              />
            </FormGroup>
          </Col>

          <Col className="d-flex h-100">
            <div className="d-flex align-items-end ms-auto">
              <Button
                color="outline-secondary"
                className="py-2 px-3 d-flex align-items-center shadow-sm"
                onClick={handlePrint}
              >
                <i className="ri-printer-line fs-16 me-2"></i>
              </Button>
            </div>
          </Col>
        </Row>

        <Card>
          <CardBody>
            <Table bordered striped>
              <thead>
                <tr>
                  <th className="table-warning text-center fw-bold" colSpan="3">
                    {organizationName} - {formatDate(selectedDate)}
                  </th>
                </tr>
                <tr>
                  <th className="table-primary fw-bold">Particulars</th>
                  <th className="table-primary fw-bold" colSpan="2">
                    Total Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-bold">Opening Balance Cash</td>
                  <td colSpan="2" className="text-end">
                    <NumberCommas number={daybookData.cashOpeningBalance} />
                  </td>
                </tr>
                <tr>
                  <td className="fw-bold">Cash Add On</td>
                  <td colSpan="2" className="text-end">
                    <NumberCommas number={daybookData.cashAdded} />
                  </td>
                </tr>

                {/* Sales Section with Accordion */}
                <tr onClick={toggleSales} style={{ cursor: "pointer" }}>
                  <td className="fw-bold">
                    Sales
                    <i
                      className={
                        isSalesOpen
                          ? "ri-arrow-up-s-line ms-2"
                          : "ri-arrow-down-s-line ms-2"
                      }
                    ></i>
                  </td>
                  <td colSpan="2" className="text-end">
                    {!isSalesOpen && (
                      <NumberCommas
                        number={daybookData.dayBookSales.totalSales}
                      />
                    )}
                  </td>
                </tr>

                {isSalesOpen && (
                  <>
                    <tr>
                      <td style={{ paddingLeft: "2rem" }}>Cash Sales</td>
                      <td colSpan="2" className="text-end">
                        <NumberCommas
                          number={daybookData.dayBookSales.cashSales}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "2rem" }}>Card Sales</td>
                      <td colSpan="2" className="text-end">
                        <NumberCommas
                          number={daybookData.dayBookSales.cardSales}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "2rem" }}>
                        Bank Sales (
                        <span
                          onClick={toggleBankSalesModal}
                          style={{
                            color: "#4da6ff",
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </span>
                        )
                      </td>

                      <td colSpan="2" className="text-end">
                        <NumberCommas
                          number={daybookData.dayBookSales.totalBankSales}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: "2rem" }}>Credit Sales</td>
                      <td colSpan="2" className="text-end">
                        <NumberCommas
                          number={daybookData.dayBookSales.creditSales}
                        />
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: "#FFFBCC" }}>
                      <td className="fw-bold">Total Sales</td>
                      <td colSpan="2" className="text-end fw-bold">
                        <NumberCommas
                          number={daybookData.dayBookSales.totalSales}
                        />
                      </td>
                    </tr>
                  </>
                )}

                <tr>
                  <td>Bank Deposit</td>
                  <td colSpan="2" className="text-end">
                    <NumberCommas number={daybookData.bankDeposit} />
                  </td>
                </tr>
                <tr>
                  <td>Debtors Received(Cash)</td>
                  <td colSpan="2" className="text-end">
                    <NumberCommas number={daybookData.debtiorsRecived} />
                  </td>
                </tr>
                <tr>
                  <td>Debtors Received(Card/Online)</td>
                  <td colSpan="2" className="text-end">
                    <NumberCommas number={daybookData.debtiorsRecivedBank} />
                  </td>
                </tr>
                <tr style={{ backgroundColor: getClosingBalanceColor() }}>
                  <td className="fw-bold">Closing Balance Cash</td>
                  <td colSpan="2" className="text-end fw-bold">
                    <NumberCommas number={daybookData.cashClosingBalance} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Bank Sales Modal */}
        <Modal isOpen={isBankSalesModalOpen} toggle={toggleBankSalesModal}>
          <ModalHeader toggle={toggleBankSalesModal}>Bank Details</ModalHeader>
          <ModalBody>
            <Table bordered striped>
              <thead>
                <tr>
                  <th>Bank Name</th>
                  <th>Account Number</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {daybookData.dayBookSales.bankSales.length > 0 ? (
                  daybookData.dayBookSales.bankSales.map((bank, index) => (
                    <tr key={index}>
                      <td>{bank.bankName}</td>
                      <td>{bank.accountNumber}</td>
                      <td className="text-end">
                        <NumberCommas number={bank.amount} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">
                      No bank sales available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>
      </LayoutWithBreadCrumb>
    </React.Fragment>
  );
};

export default Daybook;
